import { create } from 'zustand';

interface UploadStatus {
	fileName: string;
	progress: number;
	isCompleted: boolean;
	error: string | null;
	key: string;
	retryCount: number;
	isPaused: boolean;
	uploadId?: string;
	parts?: { partNumber: number; eTag: string }[];
}

interface UploadState {
	uploadStatus: UploadStatus[];
	globalLoading: boolean;
	maxRetries: number;
	maxConcurrentUploads: number;
	setUploadStatus: (index: number, status: UploadStatus) => void;
	resetStatus: (key?: string) => void;
	removeUploadStatusByFileName: (fileName: string) => void;
	handleRemove: (index: number) => void;
	emptyUploadStatus: () => void;
	setGlobalLoading: (loading: boolean) => void;
	incrementRetry: (index: number) => void;
	togglePause: (index: number) => void;
	updateParts: (index: number, parts: { partNumber: number; eTag: string }[]) => void;
}

export const useUploadStore = create<UploadState>()((set) => ({
	uploadStatus: [],
	globalLoading: false,
	maxRetries: 3,
	maxConcurrentUploads: 3,
	setUploadStatus: (index: number, status: UploadStatus) => {
		set((state) => {
			const newStatus = [...state.uploadStatus];
			newStatus[index] = {
				...newStatus[index],
				...status,
				retryCount: newStatus[index]?.retryCount || 0,
			};
			return { uploadStatus: newStatus };
		});
	},
	resetStatus: (key?: string) => {
		set((state) => {
			const newStatus = key
				? state.uploadStatus.filter((status) => status.key !== key)
				: [];
			return { uploadStatus: newStatus };
		});
	},
	removeUploadStatusByFileName: (fileName: string) => {
		set((state) => ({
			uploadStatus: state.uploadStatus.filter((status) => status.fileName !== fileName),
		}));
	},
	emptyUploadStatus: () => {
		set(() => ({ uploadStatus: [] }));
	},
	handleRemove: (index: number) => {
		set((state) => ({
			uploadStatus: state.uploadStatus.filter((_, i) => i !== index),
		}));
	},
	setGlobalLoading: (loading: boolean) => {
		set(() => ({ globalLoading: loading }));
	},
	incrementRetry: (index: number) => {
		set((state) => {
			const newStatus = [...state.uploadStatus];
			const currentStatus = newStatus[index];
			
			if (currentStatus) {
				// If status exists, increment retry count
				newStatus[index] = {
					...currentStatus,
					retryCount: (currentStatus.retryCount || 0) + 1,
				};
			} else {
				// If no status exists, create minimal status with retry count
				newStatus[index] = {
					fileName: '',
					progress: 0,
					isCompleted: false,
					error: null,
					key: '',
					retryCount: 1,
					isPaused: false,
				};
			}
			
			return { uploadStatus: newStatus };
		});
	},
	togglePause: (index: number) => {
		set((state) => {
			const newStatus = [...state.uploadStatus];
			newStatus[index] = {
				...newStatus[index],
				isPaused: !newStatus[index]?.isPaused,
			};
			return { uploadStatus: newStatus };
		});
	},
	updateParts: (index: number, parts: { partNumber: number; eTag: string }[]) => {
		set((state) => {
			const newStatus = [...state.uploadStatus];
			newStatus[index] = {
				...newStatus[index],
				parts,
			};
			return { uploadStatus: newStatus };
		});
	},
}));
